import React from 'react'
import { css } from '@emotion/core'
import { Card, colors, FlowGrid, H3, Paragraph, Separator, Skeleton, usePageWidth } from '@fs/zion-ui'

import RecordSearchForm from './RecordSearchForm'
import RecordsTable from './RecordsTable'
import AdvancedRegionalMap from '../../../../../../components/section-templates/components/Map/AdvancedRegionalMap'
import PhilippinesMap from '../../../../components/PhilippinesMap'

// Custom CSS since Zion UI Row doesn't play nice with what the design has
const legendCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  & > * {
    width: fit-content;
  }

  .legend-gradient {
    width: 100%;
    max-width: 176px;
    height: 26px;
    background: linear-gradient(
      to right,
      ${colors.blue05} 1%,
      ${colors.blue20} 34%,
      ${colors.blue50} 66%,
      ${colors.blue70} 99%
    );
    border-radius: 5px;
  }
`

export default function PhilippinesMapTab({ tabName, isLoading, provinces, surname = '' }) {
  const isMobile = usePageWidth()({ default: true, lg: false })

  let provincesToShow = provinces
  if (tabName === 'population') {
    provincesToShow = provinces?.filter((province) => province.populationCount > 0)
  }

  if (!isLoading && !provincesToShow) return null

  const top5Records =
    provincesToShow
      ?.sort((a, b) => {
        return tabName === 'population' ? b.populationCount - a.populationCount : b.recordCount - a.recordCount
      })
      ?.slice(0, 5) || []

  const numberLabel = `${isMobile ? '# of ' : 'Number of '}${tabName === 'population' ? 'People' : 'Records'}`

  return (
    <FlowGrid columnCount={isMobile ? 1 : 2} gutters={isMobile ? 'md' : 'xl'}>
      <Card alignY="middle" backgroundColor={colors.gray02} edges="none">
        <AdvancedRegionalMap
          tabName={tabName}
          regions={provincesToShow}
          rankAttribute={tabName === 'population' ? 'populationRank' : 'recordRank'}
          maxWidth={364}
          alignY="middle"
          rankColors={[colors.blue70, colors.blue50, colors.blue20]}
          mapColor={colors.blue05}
        >
          <PhilippinesMap />
        </AdvancedRegionalMap>
        <Separator size="md" />
        <div css={legendCss}>
          <Paragraph size="xs">Fewer {tabName === 'population' ? 'People' : 'Records'}</Paragraph>
          <div className="legend-gradient" />
          <Paragraph size="xs">More {tabName === 'population' ? 'People' : 'Records'}</Paragraph>
        </div>
      </Card>
      <div>
        <RecordsTable
          loading={isLoading}
          top5={top5Records}
          labels={['Top 5 Provinces', numberLabel]}
          countAttribute={tabName === 'population' ? 'populationCount' : 'recordCount'}
          surname={surname}
          withRecordLink={tabName === 'records'}
        />
        <Separator />
        <div>
          {isLoading ? <Skeleton.H3 data-testid="Ph.MapTab.Skeleton.H3" /> : <H3 supplementary>Find a Ninuno</H3>}
          <RecordSearchForm surname={surname} />
        </div>
      </div>
    </FlowGrid>
  )
}
